import React, {memo, useState} from 'react';
import {
    Avatar, Box, Button, ButtonGroup, Checkbox, Chip, CircularProgress, Collapse, IconButton, LinearProgress, Link as MuiLink, Menu,
    MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScheduleIcon from '@mui/icons-material/Schedule';

export const fmt = (n) => String(Math.round(n * 100) / 100);
const parse = (v) => parseFloat(String(v).replace(',', '.'));
const initials = (name) => (name || '?').split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

const AI_RUN = {OK: ['AI проверил', 'secondary'], SKIPPED: ['AI пропустил', 'default'], ERROR: ['AI: ошибка', 'error']};
const SCRIPT_RUN = {OK: ['Автопроверка', 'secondary'], SKIPPED: ['Пропущено', 'default'], ERROR: ['Ошибка проверки', 'error']};

// Редактор баллов одного студента. Черновики держим внутри (локальное состояние), а не на уровне страницы: правка одного
// поля не перерисовывает весь список — раньше именно это и подвешивало страницу на больших группах.
const Editor = ({student, criteria, onSave, busy, aiType, canAi, onAi, scripts}) => {
    const [drafts, setDrafts] = useState(() => {
        const d = {};
        criteria.forEach((c) => {
            const r = student.results[c.id];
            d[c.id] = {points: r ? String(r.earnedPoints) : '', comment: r?.comment || ''};
        });
        return d;
    });
    const [openDesc, setOpenDesc] = useState({});
    const set = (id, patch) => setDrafts((prev) => ({...prev, [id]: {...prev[id], ...patch}}));
    const total = criteria.reduce((sum, c) => sum + (parse(drafts[c.id].points) || 0), 0);
    const max = criteria.reduce((sum, c) => sum + c.maxPoints, 0);
    const submit = (published) => onSave(student, criteria.map((c) => ({
        criterionId: c.id, earnedPoints: parse(drafts[c.id].points) || 0, comment: drafts[c.id].comment,
    })), published);

    return (
        <Box sx={{px: {xs: 1.5, sm: 3}, pb: 2, pt: 1}}>
            <Stack spacing={1}>
                {criteria.map((c) => {
                    const d = drafts[c.id];
                    const r = student.results[c.id];
                    const value = parse(d.points);
                    const over = Number.isFinite(value) && value > c.maxPoints;
                    return (
                        <Box key={c.id} sx={{border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5}}>
                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
                                <Typography variant="body2" fontWeight={600} sx={{minWidth: 0}}>
                                    {c.title}
                                    {!c.visible && <Chip size="small" label="скрытый" sx={{ml: 1, height: 18}}/>}
                                    {r?.source === 'AI' && <Chip size="small" color="secondary" variant="outlined" icon={<AutoAwesomeIcon/>} label="AI" sx={{ml: 1, height: 18}}/>}
                                    {r?.source === 'AUTO' && <Chip size="small" color="secondary" variant="outlined" icon={<AutoAwesomeIcon/>} label="авто" sx={{ml: 1, height: 18}}/>}
                                    {c.description && (
                                        <Typography component="span" variant="caption" color="primary" sx={{ml: 1, cursor: 'pointer'}}
                                                    onClick={() => setOpenDesc((o) => ({...o, [c.id]: !o[c.id]}))}>
                                            {openDesc[c.id] ? 'скрыть рубрику' : 'рубрика'}
                                        </Typography>
                                    )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>макс. {fmt(c.maxPoints)}</Typography>
                            </Stack>
                            <Collapse in={!!openDesc[c.id]} unmountOnExit>
                                <Typography variant="caption" color="text.secondary" sx={{whiteSpace: 'pre-wrap', display: 'block', mt: 0.5}}>{c.description}</Typography>
                            </Collapse>
                            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{mt: 1}} alignItems={{sm: 'center'}}>
                                <TextField size="small" type="number" label="Баллы" value={d.points} error={over}
                                           onChange={(e) => set(c.id, {points: e.target.value})}
                                           slotProps={{htmlInput: {min: 0, max: c.maxPoints, step: 0.25}}} sx={{width: 110, flexShrink: 0}}/>
                                <ButtonGroup size="small" variant="outlined" sx={{flexShrink: 0}}>
                                    <Button onClick={() => set(c.id, {points: '0'})}>0</Button>
                                    <Button onClick={() => set(c.id, {points: String(c.maxPoints / 2)})}>½</Button>
                                    <Button onClick={() => set(c.id, {points: String(c.maxPoints)})}>макс</Button>
                                </ButtonGroup>
                                <TextField size="small" fullWidth label="Комментарий студенту" value={d.comment}
                                           onChange={(e) => set(c.id, {comment: e.target.value})}/>
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems={{sm: 'center'}} justifyContent="space-between" sx={{mt: 2}}>
                <Typography variant="body2" color="text.secondary">Итого: <b>{fmt(total)}</b> из {fmt(max)}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {aiType && (
                        <Button color="secondary" startIcon={<AutoAwesomeIcon/>} disabled={!canAi} onClick={() => onAi(student)}>{scripts ? 'Перепроверить' : 'Перепроверить AI'}</Button>
                    )}
                    {student.published && <Button color="warning" disabled={busy} onClick={() => submit(false)}>Снять публикацию</Button>}
                    <Button disabled={busy} onClick={() => submit(undefined)}>Сохранить</Button>
                    <Button variant="contained" disabled={busy} onClick={() => submit(true)}>
                        {student.published ? 'Сохранить и обновить' : 'Сохранить и опубликовать'}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

// Строка студента: статус, баллы, одно главное действие и меню. Мемоизирована: список из десятков студентов не перерисовывается целиком.
const StudentRow = memo(({student, criteria, totalMax, run, aiState, selected, onSelect, expanded, onToggle, onSave, onPublish, onAi,
                         aiType, canAi, busy, scripts}) => {
    const runLabels = scripts ? SCRIPT_RUN : AI_RUN;
    const [anchor, setAnchor] = useState(null);
    const earned = Object.values(student.results).reduce((sum, r) => sum + r.earnedPoints, 0);
    const hasResults = Object.keys(student.results).length > 0;
    const ratio = totalMax > 0 ? Math.min(1, earned / totalMax) : 0;
    const state = student.published ? 'published' : hasResults ? 'draft' : 'todo';

    const statusChip = {
        published: <Chip size="small" color="success" label={student.autoPublished ? 'Опубликовано (AI)' : 'Опубликовано'}/>,
        draft: <Chip size="small" color="warning" variant="outlined" label="Черновик"/>,
        todo: <Chip size="small" variant="outlined" label="Не проверено"/>,
    }[state];

    return (
        <Box sx={{borderBottom: 1, borderColor: 'divider', bgcolor: expanded ? 'action.hover' : 'transparent'}}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{px: {xs: 1, sm: 2}, py: 1}} flexWrap={{xs: 'wrap', md: 'nowrap'}} useFlexGap>
                <Checkbox size="small" checked={selected} onChange={() => onSelect(student.userId)} sx={{p: 0.5}}/>
                <Avatar sx={{width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main'}}>{initials(student.fullName)}</Avatar>
                <Box sx={{minWidth: 0, flex: '1 1 180px', cursor: 'pointer'}} onClick={() => onToggle(student.userId)}>
                    <Typography variant="body2" fontWeight={600} noWrap>{student.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {[student.groupName, student.partnerName && `в паре: ${student.partnerName}`].filter(Boolean).join(' · ')}
                        {student.resourceUrl && (
                            <> · <MuiLink href={student.resourceUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>репозиторий ↗</MuiLink></>
                        )}
                    </Typography>
                </Box>

                {aiState === 'running' && <Chip size="small" color="secondary" icon={<CircularProgress size={12} color="inherit"/>} label="Проверяется…"/>}
                {aiState === 'queued' && <Chip size="small" variant="outlined" icon={<ScheduleIcon/>} label="В очереди"/>}
                {!aiState && run && runLabels[run.status] && (
                    <Tooltip title={run.message || ''}>
                        <Chip size="small" variant="outlined" color={runLabels[run.status][1]} icon={<AutoAwesomeIcon/>} label={runLabels[run.status][0]}/>
                    </Tooltip>
                )}

                <Box sx={{width: 120, flexShrink: 0}}>
                    <Typography variant="caption" color="text.secondary">{hasResults ? `${fmt(earned)} / ${fmt(totalMax)}` : '— / ' + fmt(totalMax)}</Typography>
                    <LinearProgress variant="determinate" value={ratio * 100} color={ratio >= 0.75 ? 'success' : ratio >= 0.4 ? 'warning' : 'error'}
                                    sx={{height: 5, borderRadius: 3, opacity: hasResults ? 1 : 0.3}}/>
                </Box>

                {statusChip}

                {state === 'draft' ? (
                    <Button size="small" variant="contained" color="success" disabled={busy} onClick={() => onPublish(student, true)}>Опубликовать</Button>
                ) : state === 'todo' && aiType ? (
                    <Button size="small" variant="outlined" color="secondary" startIcon={<AutoAwesomeIcon/>} disabled={!canAi || !!aiState} onClick={() => onAi(student)}>
                        Проверить
                    </Button>
                ) : null}

                <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label="Действия"><MoreVertIcon fontSize="small"/></IconButton>
                <IconButton size="small" onClick={() => onToggle(student.userId)} aria-label="Баллы по критериям"
                            sx={{transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s'}}>
                    <ExpandMoreIcon/>
                </IconButton>
                <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                    {aiType && (
                        <MenuItem disabled={!canAi || !!aiState || (student.published && !student.autoPublished)}
                                  onClick={() => { setAnchor(null); onAi(student); }}>
                            {hasResults ? (scripts ? 'Перепроверить' : 'Перепроверить AI') : (scripts ? 'Проверить' : 'Проверить AI')}
                        </MenuItem>
                    )}
                    {student.published
                        ? <MenuItem disabled={busy} onClick={() => { setAnchor(null); onPublish(student, false); }}>Вернуть в черновик</MenuItem>
                        : <MenuItem disabled={busy || !hasResults} onClick={() => { setAnchor(null); onPublish(student, true); }}>Опубликовать</MenuItem>}
                    <MenuItem onClick={() => { setAnchor(null); onToggle(student.userId); }}>{expanded ? 'Свернуть баллы' : 'Править баллы'}</MenuItem>
                </Menu>
            </Stack>

            {/* Редактор монтируется только у раскрытой строки — иначе в DOM сотни полей ввода на каждого студента. */}
            <Collapse in={expanded} unmountOnExit timeout={150}>
                <Editor student={student} criteria={criteria} onSave={onSave} busy={busy} aiType={aiType} canAi={canAi && !aiState} onAi={onAi} scripts={scripts}/>
            </Collapse>
        </Box>
    );
});

export default StudentRow;
